import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { CircleCheck as CheckCircle, ArrowRight, Phone, Sparkles, Target, Globe, Palette, FileText, Server, Shield, Smartphone, Lock, Zap, Users, Briefcase, Eye, Search, Cloud, Settings, TrendingUp } from 'lucide-react';

const WebseitenErstellung = () => {
  const seoJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Webseiten-Erstellung Mönchengladbach',
    description: 'Professionelle Webseiten für KMU in Mönchengladbach: modern, DSGVO-konform, sicher und technisch sauber umgesetzt. Persönliche Betreuung inklusive.',
    provider: { '@type': 'LocalBusiness', name: 'Schteffann', url: 'https://schteffann.de/' },
    areaServed: ['Mönchengladbach', 'Viersen', 'Krefeld', 'Neuss', 'Düsseldorf', 'Nordrhein-Westfalen'],
    serviceType: 'Webseiten-Erstellung'
  };

  const probleme = [
    'Veraltete Webseiten',
    'Unsichere Systeme',
    'Fehlende Mobiloptimierung',
    'Lange Ladezeiten',
    'Komplizierte Pflege',
    'Fehlende DSGVO-Konformität'
  ];

  const technischeUmsetzung = [
    { icon: Zap, text: 'Moderne, schnelle und sichere Webseiten' },
    { icon: Smartphone, text: 'Mobile-Optimierung (Responsive Design)' },
    { icon: Shield, text: 'DSGVO-konforme Umsetzung' },
    { icon: FileText, text: 'Cookie-Banner & Datenschutzerklärung' },
    { icon: Lock, text: 'SSL-Verschlüsselung' },
    { icon: Eye, text: 'Barrierearme Gestaltung' }
  ];

  const designStruktur = [
    { icon: Palette, text: 'Klarer, moderner Aufbau' },
    { icon: Sparkles, text: 'Unternehmensbezogene Farbwelt' },
    { icon: Globe, text: 'Professionelle Bild- und Icon-Auswahl' },
    { icon: Target, text: 'Übersichtliche Navigation' },
    { icon: Users, text: 'Fokus auf Nutzerfreundlichkeit' }
  ];

  const inhalte = [
    { icon: FileText, text: 'Erstellung oder Optimierung von Texten' },
    { icon: Settings, text: 'Strukturierung der Inhalte' },
    { icon: Globe, text: 'Leistungsseiten, Kontaktseite, Teamseite' },
    { icon: Search, text: 'Lokale SEO-Optimierung für Mönchengladbach' }
  ];

  const hosting = [
    { icon: Server, text: 'Sichere Hosting-Lösungen' },
    { icon: TrendingUp, text: 'Regelmäßige Updates' },
    { icon: Shield, text: 'Backup-Strategien' },
    { icon: Phone, text: 'Technische Betreuung & Support' },
    { icon: Settings, text: 'Erweiterungen und Anpassungen nach Bedarf' }
  ];

  const zielgruppe = [
    'Kleine und mittelständische Unternehmen',
    'Handwerksbetriebe',
    'Pflege- und Betreuungsdienste',
    'Selbstständige & Freiberufler',
    'Lokale Dienstleister',
    'Vereine und Organisationen'
  ];

  const vorteile = [
    'Technische Umsetzung',
    'Design',
    'Inhalte',
    'Hosting',
    'Sicherheit',
    'Wartung'
  ];

  const vorgehen = [
    {
      step: '1',
      title: 'Kostenloses Erstgespräch',
      description: 'Wir klären Ziele, Inhalte und Anforderungen.'
    },
    {
      step: '2',
      title: 'Konzept & Struktur',
      description: 'Ich erstelle eine klare Seitenstruktur und Designvorschläge.'
    },
    {
      step: '3',
      title: 'Umsetzung',
      description: 'Technische Entwicklung, Design, Inhalte, SEO-Optimierung.'
    },
    {
      step: '4',
      title: 'Abnahme & Go-Live',
      description: 'Sie prüfen alles in Ruhe, danach geht die Seite online.'
    },
    {
      step: '5',
      title: 'Wartung & Betreuung',
      description: 'Auf Wunsch übernehme ich Updates, Backups und Support.'
    }
  ];

  const warum = [
    'Persönliche Betreuung statt anonymer Agentur',
    'Schnelle Umsetzung',
    'Technische Expertise aus der IT-Administration',
    'Fokus auf Sicherheit & Stabilität',
    'Faire, transparente Preise',
    'Langfristige Zusammenarbeit möglich'
  ];

  const relatedPages = [
    { title: 'IT-Administrator', url: '/it-administrator-moenchengladbach', description: 'Technische Betreuung Ihrer IT-Systeme' },
    { title: 'IT-Leiter für KMU', url: '/it-leiter-kmu', description: 'Strategische IT-Führung für Ihr Unternehmen' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEOHead
        title="Webseiten-Erstellung für KMU in Mönchengladbach – Modern, sicher & professionell"
        description="Professionelle Webseiten für KMU in Mönchengladbach: modern, DSGVO-konform, sicher und technisch sauber umgesetzt. Persönliche Betreuung inklusive. Raum Mönchengladbach, Viersen, Krefeld."
        keywords="Webseiten Erstellung Mönchengladbach, Website erstellen KMU, Webdesign Mönchengladbach, DSGVO Website, professionelle Homepage, Viersen, Krefeld, NRW"
        jsonLd={seoJsonLd}
      />
      {/* Hero Section */}
      <section className="relative py-20 lg:py-24 overflow-hidden bg-gradient-to-br from-cyan-50/50 via-blue-50/30 to-slate-50">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-blue-600/5"></div>
        <div className="absolute top-10 right-10 w-72 h-72 bg-gradient-to-r from-cyan-400/15 to-blue-500/15 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-gradient-to-r from-blue-400/10 to-cyan-500/10 rounded-full blur-3xl"></div>

        <div className="container relative">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-gradient-to-r from-cyan-100 to-blue-100 text-cyan-800 border-cyan-200 px-6 py-2">
              <Globe className="w-4 h-4 mr-2" />
              Webseiten-Erstellung
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-slate-900">Webseiten-Erstellung für Unternehmen in </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-700">Mönchengladbach</span>
            </h1>
            <p className="text-xl lg:text-2xl text-slate-600 leading-relaxed">
              Eine moderne Website ist heute mehr als nur eine digitale Visitenkarte. Sie ist der zentrale Anlaufpunkt für Kunden, Partner und Bewerber. Ich unterstütze kleine und mittelständische Unternehmen in Mönchengladbach dabei, professionelle, sichere und technisch saubere Webseiten zu erstellen – klar strukturiert, schnell, DSGVO-konform und perfekt auf Ihr Unternehmen abgestimmt.
            </p>
          </div>
        </div>
      </section>

      {/* Warum wichtig Section */}
      <section className="py-20">
        <div className="container">
          <div className="mb-20">
            <Card className="bg-gradient-to-br from-slate-50 to-cyan-50/30 border-0 shadow-xl">
              <CardContent className="p-12 lg:p-16">
                <div className="text-center mb-12">
                  <Badge className="mb-6 bg-cyan-100 text-cyan-800 border-cyan-200 px-6 py-2">
                    <Target className="w-4 h-4 mr-2" />
                    Herausforderungen
                  </Badge>
                  <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                    Warum eine professionelle Website wichtig ist
                  </h2>
                  <p className="text-xl text-slate-600 mb-8">Viele KMU kämpfen mit:</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
                  {probleme.map((problem, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="h-6 w-6 text-cyan-600 mt-1 flex-shrink-0" />
                      <p className="text-lg text-slate-700">{problem}</p>
                    </div>
                  ))}
                </div>
                <p className="text-center text-xl text-slate-700 mt-12 max-w-3xl mx-auto">
                  Ich helfe Ihnen, diese Probleme nachhaltig zu lösen – mit einer Website, die technisch sauber, modern und zuverlässig ist.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Leistungen */}
          <div className="text-center mb-16">
            <Badge className="mb-6 bg-cyan-100 text-cyan-800 border-cyan-200 px-6 py-2">
              <Settings className="w-4 h-4 mr-2" />
              Leistungen
            </Badge>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Meine Leistungen im Bereich Webseiten-Erstellung
            </h2>
          </div>

          {/* Technische Umsetzung */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
              <div className="p-2 bg-cyan-100 rounded-lg mr-3">
                <Settings className="h-6 w-6 text-cyan-600" />
              </div>
              Technische Umsetzung
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {technischeUmsetzung.map((item, index) => (
                <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-slate-200 hover:border-cyan-200">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="p-3 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl group-hover:from-cyan-200 group-hover:to-blue-200 transition-all flex-shrink-0">
                        <item.icon className="h-6 w-6 text-cyan-600" />
                      </div>
                      <p className="text-slate-700 leading-relaxed pt-2">
                        {item.text}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Design & Struktur */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
              <div className="p-2 bg-cyan-100 rounded-lg mr-3">
                <Palette className="h-6 w-6 text-cyan-600" />
              </div>
              Design & Struktur
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {designStruktur.map((item, index) => (
                <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-slate-200 hover:border-cyan-200">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="p-3 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl group-hover:from-cyan-200 group-hover:to-blue-200 transition-all flex-shrink-0">
                        <item.icon className="h-6 w-6 text-cyan-600" />
                      </div>
                      <p className="text-slate-700 leading-relaxed pt-2">
                        {item.text}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Inhalte & Strukturierung */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
              <div className="p-2 bg-cyan-100 rounded-lg mr-3">
                <FileText className="h-6 w-6 text-cyan-600" />
              </div>
              Inhalte & Strukturierung
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              {inhalte.map((item, index) => (
                <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-slate-200 hover:border-cyan-200">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="p-3 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl group-hover:from-cyan-200 group-hover:to-blue-200 transition-all flex-shrink-0">
                        <item.icon className="h-6 w-6 text-cyan-600" />
                      </div>
                      <p className="text-slate-700 leading-relaxed pt-2">
                        {item.text}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Hosting, Wartung & Betreuung */}
          <div className="mb-20">
            <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
              <div className="p-2 bg-cyan-100 rounded-lg mr-3">
                <Server className="h-6 w-6 text-cyan-600" />
              </div>
              Hosting, Wartung & Betreuung
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {hosting.map((item, index) => (
                <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-slate-200 hover:border-cyan-200">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="p-3 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl group-hover:from-cyan-200 group-hover:to-blue-200 transition-all flex-shrink-0">
                        <item.icon className="h-6 w-6 text-cyan-600" />
                      </div>
                      <p className="text-slate-700 leading-relaxed pt-2">
                        {item.text}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Zielgruppe */}
          <div className="mb-20">
            <Card className="bg-gradient-to-br from-slate-50 to-cyan-50/30 border-0 shadow-xl">
              <CardContent className="p-12 lg:p-16">
                <div className="text-center mb-12">
                  <Badge className="mb-6 bg-cyan-100 text-cyan-800 border-cyan-200 px-6 py-2">
                    <Briefcase className="w-4 h-4 mr-2" />
                    Zielgruppe
                  </Badge>
                  <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                    Für wen ist dieses Angebot ideal?
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto mb-8">
                  {zielgruppe.map((item, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="h-6 w-6 text-cyan-600 mt-1 flex-shrink-0" />
                      <p className="text-lg text-slate-700">{item}</p>
                    </div>
                  ))}
                </div>
                <p className="text-center text-xl text-slate-700 max-w-3xl mx-auto">
                  <strong>Kurz:</strong> Für alle, die eine moderne, sichere und zuverlässige Website benötigen – ohne komplizierte Agenturprozesse.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Alles aus einer Hand */}
          <div className="mb-20">
            <Card className="border-2 border-cyan-200 shadow-xl">
              <CardContent className="p-12">
                <div className="text-center mb-8">
                  <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                    Ihr Vorteil: Alles aus einer Hand
                  </h2>
                  <p className="text-xl text-slate-600 mb-8">Sie erhalten:</p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 max-w-4xl mx-auto">
                  {vorteile.map((vorteil, index) => (
                    <div key={index} className="text-center">
                      <div className="p-4 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-2xl mb-3 inline-block">
                        <CheckCircle className="h-8 w-8 text-cyan-600" />
                      </div>
                      <p className="text-slate-700 font-medium">{vorteil}</p>
                    </div>
                  ))}
                </div>
                <p className="text-center text-lg text-slate-600 mt-8 max-w-3xl mx-auto">
                  … und das mit einem <strong>persönlichen Ansprechpartner</strong>, der Ihre IT-Struktur versteht und langfristig begleitet.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Vorgehen */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <Badge className="mb-6 bg-cyan-100 text-cyan-800 border-cyan-200 px-6 py-2">
                <Target className="w-4 h-4 mr-2" />
                Prozess
              </Badge>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                Mein Vorgehen – transparent & strukturiert
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              {vorgehen.map((item, index) => (
                <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-slate-200 hover:border-cyan-200">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-cyan-100 to-blue-100 text-cyan-700 rounded-2xl flex items-center justify-center mx-auto mb-4 font-bold text-2xl group-hover:from-cyan-200 group-hover:to-blue-200 transition-all">
                      {item.step}
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-3">{item.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Warum mit mir arbeiten */}
          <div className="mb-20">
            <Card className="bg-gradient-to-br from-slate-50 to-cyan-50/30 border-0 shadow-xl">
              <CardContent className="p-12 lg:p-16">
                <div className="text-center mb-12">
                  <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                    Warum Unternehmen in Mönchengladbach mit mir arbeiten
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
                  {warum.map((item, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="h-6 w-6 text-cyan-600 mt-1 flex-shrink-0" />
                      <p className="text-lg text-slate-700">{item}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Related Pages */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <Badge className="mb-6 bg-cyan-100 text-cyan-800 border-cyan-200 px-6 py-2">
                <Target className="w-4 h-4 mr-2" />
                Weitere Leistungen
              </Badge>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                Das könnte Sie auch interessieren
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {relatedPages.map((page, index) => (
                <Link key={index} to={page.url}>
                  <Card className="group hover:shadow-xl transition-all duration-300 border-slate-200 hover:border-cyan-200 h-full">
                    <CardContent className="p-8">
                      <h3 className="text-xl font-bold text-slate-900 group-hover:text-cyan-700 transition-colors mb-3">
                        {page.title}
                      </h3>
                      <p className="text-slate-600 mb-4">{page.description}</p>
                      <div className="flex items-center text-cyan-600 group-hover:text-cyan-700">
                        <span className="text-sm font-medium">Mehr erfahren</span>
                        <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <Card className="bg-gradient-to-br from-cyan-600 via-blue-600 to-cyan-700 border-0 shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
            <CardContent className="p-12 lg:p-16 relative text-white text-center">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Sie möchten eine neue Website erstellen lassen oder Ihre bestehende modernisieren?
              </h2>
              <p className="text-xl text-cyan-100 mb-10 max-w-2xl mx-auto">
                Ich freue mich auf ein unverbindliches Gespräch.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Button size="lg" asChild className="bg-white text-cyan-700 hover:bg-cyan-50 shadow-xl px-8 py-4 text-lg">
                  <Link to="/contact">
                    <Target className="mr-2 h-5 w-5" />
                    Jetzt Kontakt aufnehmen
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-4 text-lg">
                  <a href="tel:015679697252">
                    <Phone className="mr-2 h-5 w-5" />
                    0156-79697252
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default WebseitenErstellung;
